/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   DashboardTab.tsx
 *     Feature         :   254-pipeline-monitoring-overhaul (US4) — rewrite of the Spec-206 tab.
 *     Description     :   Attention-feed-led dashboard (FR-027a). The ranked "needs attention"
 *                         feed leads; then new arrivals; then a labelled "Fleet metrics" block
 *                         (fleet health + a KPI strip with per-tile explanations, incl. p50/p95
 *                         run latency and throughput) governed by a window selector; then a
 *                         described status panel and labelled charts. Every section carries a
 *                         header + one-line description so a viewer knows what they're looking
 *                         at (Spec 254). Kit + tokens; recharts only.
 *     Constitution    :   Principle XLV, XXXVII
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-24
 * =========================================================================================
 */
import { useState } from 'react';
import { AlertTriangle, Activity } from 'lucide-react';
import { Card, DashboardPanel, StatCard, Badge, Button, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { usePipelineDashboard } from '@/hooks/usePipelineMonitoring';
import { AttentionFeed } from './AttentionFeed';
import { NewArrivalsPanel } from './NewArrivalsPanel';
import { FleetHealthTile } from './FleetHealthTile';
import { MonitoringCharts } from './MonitoringCharts';

const WINDOWS = ['24h', '7d', '30d'] as const;
const DANGER = 'text-[var(--color-status-danger)]';
const WARN = 'text-[var(--color-status-warning)]';

function pct(n: number): string { return `${(n * 100).toFixed(1)}%`; }
function fmtDur(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}
/** Record COUNTS, not bytes — decimal is correct here (1e9 records renders as "1.0B"). */
function fmtCount(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`; // not-bytes
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`; // not-bytes
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}

export function DashboardTab(): JSX.Element {
  const [window, setWindow] = useState<string>('7d');
  const { data, isLoading, isError, refetch } = usePipelineDashboard(window);

  if (isLoading) { return <LoadingState />; }
  if (isError || !data) { return <ErrorState onRetry={() => void refetch()} />; }

  const feed = data.attentionFeed ?? [];
  const unmonitored = data.unmonitored ?? false;
  const k = data.kpis;
  // FR-028: each headline metric's direction-of-travel, derived from the daily chart series
  // already computed for the window (real data, not a synthetic single point).
  const c = data.charts;
  const runsTrend = c.runVolume.map((p) => ({ date: p.bucket, value: p.runs ?? 0 }));
  const rowsTrend = c.runVolume.map((p) => ({ date: p.bucket, value: p.rowsProcessed ?? 0 }));
  const failuresTrend = c.successFailureOverTime.map((p) => ({ date: p.bucket, value: p.failure ?? 0 }));
  // Spec 298 FR-004: a day with no finished run has no success rate; it is left out of the line, never plotted as 0%
  const successTrend = c.successFailureOverTime.flatMap((p) => {
    const tot = (p.success ?? 0) + (p.failure ?? 0);
    return tot > 0 ? [{ date: p.bucket, value: Math.round(((p.success ?? 0) / tot) * 100) }] : [];
  });
  const durTrend = c.durationTrend.map((p) => ({ date: p.bucket, value: p.observedMs ?? 0 }));

  return (
    <div className="space-y-4">
      {data.degraded && (
        <Card className="border-[var(--color-status-warning)]">
          <div className="flex items-center gap-2 text-[var(--color-status-warning)]">
            <AlertTriangle size={16} />
            <span className="text-sm">Logs engine degraded — figures may understate reality. {data.reason}</span>
          </div>
        </Card>
      )}

      {unmonitored ? (
        <EmptyState title="Nothing is being monitored" message="No active pipelines. New pipelines appear here as they start reporting; adopt them in Management." />
      ) : (
        <>
          {/* 1. LEAD — the ranked attention feed (FR-027a) */}
          <AttentionFeed items={feed} />

          {/* 2. New arrivals (hidden when none) */}
          <NewArrivalsPanel />

          {/* 3. Fleet metrics — window-scoped KPIs + charts. The feed above is current-state. */}
          <div className="flex flex-wrap items-end justify-between gap-3 border-t border-[var(--color-border)] pt-4">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Fleet metrics</h3>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Health, throughput, and run latency across all monitored pipelines for the selected window. Retired pipelines are excluded.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[var(--color-text-secondary)]">
                <Activity size={12} className="mr-1 inline" />{k.runningNow} running now
                {(data.excludedRetiredCount ?? 0) > 0 ? ` · ${data.excludedRetiredCount} retired excluded` : ''}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-xs text-[var(--color-text-secondary)]">Window:</span>
                {WINDOWS.map((w) => (
                  <Button key={w} variant={w === window ? 'primary' : 'secondary'} size="sm" onClick={() => setWindow(w)}>{w}</Button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FleetHealthTile health={data.fleetHealth} />
            <StatCard label="Runs" value={k.runsInWindow.toLocaleString()} subtext="Runs started in the window"
              trend={runsTrend} trendLabel="Runs / day" trendValueFmt={(n) => String(Math.round(n))}
              tooltip="Every pipeline run whose start time falls inside the selected window." />
            <StatCard label="Success (7d)" value={pct(k.successRate7d)} subtext="Healthy ÷ all runs · healthy ≥ 95%"
              valueClassName={k.successRate7d < 0.95 ? WARN : undefined}
              trend={successTrend} trendLabel="Daily success %" trendValueFmt={(n) => `${n}%`}
              tooltip="Healthy runs (excludes failed, errored, and abandoned) as a share of all runs over the last 7 days. Healthy ≥ 95%." />
            <StatCard label="Failures (24h)" value={k.failures24h} subtext="Failed or abandoned · 0 is healthy"
              valueClassName={k.failures24h ? DANGER : undefined}
              trend={failuresTrend} trendLabel="Failures / day" trendValueFmt={(n) => String(Math.round(n))}
              tooltip="Runs that failed, errored, completed-with-errors, or hung past 24h in the last day." />
            <StatCard label="Median duration" value={fmtDur(k.medianDurationMs)} subtext="Typical run time (p50)"
              trend={durTrend} trendLabel="Avg run time / day" trendValueFmt={(n) => fmtDur(n)}
              tooltip="The median (50th-percentile) run duration in the window — the typical run." />
            <StatCard label="p95 duration" value={fmtDur(k.p95DurationMs)} subtext="Slowest 5% (p95) — tail latency"
              trend={durTrend} trendLabel="Avg run time / day" trendValueFmt={(n) => fmtDur(n)}
              tooltip="The 95th-percentile run duration — your slowest runs. Watch this against your SLO." />
            <StatCard label="Rows processed" value={fmtCount(k.rowsProcessedInWindow)} subtext="Total rows moved in the window"
              trend={rowsTrend} trendLabel="Rows / day" trendValueFmt={(n) => fmtCount(n)}
              tooltip="Sum of rows processed across every run in the window — overall throughput." />
            <StatCard label="At risk" value={k.atRisk} subtext="Open predictability findings · 0 is healthy"
              valueClassName={k.atRisk ? WARN : undefined}
              tooltip="Pipelines with an open predictability finding (missed schedule, run-time outlier, or hang)." />
          </div>

          {/* 4. Status distribution */}
          <DashboardPanel title="Status distribution" subtitle="Run counts by status in the window — green counts healthy, red counts failing.">
            <div className="flex flex-wrap gap-2">
              {data.charts.statusDistribution.length === 0 ? (
                <span className="text-sm text-[var(--color-text-secondary)]">No runs in window.</span>
              ) : data.charts.statusDistribution.map((s) => (
                <Badge key={s.status} variant={s.healthy ? 'success' : 'danger'}>{s.status}: {s.count.toLocaleString()}</Badge>
              ))}
            </div>
          </DashboardPanel>

          {/* 5. Labelled charts (each carries its own description) */}
          <MonitoringCharts charts={data.charts} />
        </>
      )}
    </div>
  );
}
